import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import * as topojson from 'topojson-client';
import ukRegions from './uk-regions.json';
import {
  FormatUtils,
  getMapColors,
  getPopulationPerc,
  isReach,
  MAP_CHART_FIELDS_LABEL,
  METRICS,
  useWindowSize
} from '../../utils';
import uniqid from 'uniqid';
import { localPoint } from '@visx/event';
import { useTooltip } from '@visx/tooltip';
import { TooltipData } from '../../components/ChartTooltip/ChartTooltip';
import {
  StyledUkMapChartFeature,
  StyledUkMapChartTooltip,
  StyledUkMapChartWrapper
} from './UkMapChart.styles';
import { Group } from '@visx/group';
import { LinearGradient } from '@visx/gradient';
import { Text } from '@visx/text';
import { scaleLinear } from '@visx/scale';
import { BoxV2, EmptyStateV2 } from '@cubeyou-team/hx-react-components';
import ChartTooltip from '../../components/ChartTooltip';
import { Albers } from '@visx/geo';

interface FeatureShape {
  type: 'Feature';
  id: string;
  geometry: { coordinates: [number, number][][]; type: 'Polygon' };
  properties: { LAD13CD: string; LAD13NM: string };
}
const topology = ukRegions as any;

const { features: regions } = topojson.feature(
  topology,
  topology.objects.lad
) as {
  type: 'FeatureCollection';
  features: FeatureShape[];
};

const districts = {
  border: [
    'Dumfries and Galloway',
    'Scottish Borders',
    'Carlisle',
    'Allerdale',
    'Copeland',
    'Eden',
    'Barrow-in-Furness'
  ],
  east: [
    'Breckland',
    'Broadland',
    'North Norfolk',
    'Great Yarmouth',
    'Waveney',
    'South Norfolk',
    'Norwich',
    'Broadland',
    'Suffolk Coastal',
    'Ipswich',
    'Mid Suffolk',
    "King's Lynn and West Norfolk",
    'St Edmundsbury',
    'Forest Heath',
    'Tendring',
    'Colchester',
    'Babergh',
    'Maldon',
    'Braintree',
    'East Cambridgeshire',
    'South Cambridgeshire',
    'Cambridge',
    'Huntingdonshire',
    'Fenland',
    'Daventry',
    'Kettering',
    'Corby',
    'East Northamptonshire',
    'Peterborough',
    'South Northamptonshire',
    'Northampton',
    'Wellingborough',
    'Bedford',
    'Milton Keynes',
    'Central Bedfordshire',
    'North Hertfordshire',
    'Uttlesford',
    'Stevenage',
    'Luton'
  ],
  london: [
    'Wandsworth',
    'Redbridge',
    'Newham',
    'Hillingdon',
    'Barnet',
    'Lambeth',
    'Tower Hamlets',
    'Sutton',
    'Richmond upon Thames',
    'Havering',
    'Westminster',
    'Haringey',
    'Mole Valley',
    'Croydon',
    'Bromley',
    'Hillingdon',
    'Enfield',
    'Reigate and Banstead',
    'Tandridge',
    'Sevenoaks',
    'Dartford',
    'Gravesham',
    'Bexley',
    'Greenwich',
    'Lewisham',
    'Southwark',
    'Islington',
    'Waltham Forest',
    'Medway',
    'Waverley',
    'Guildford',
    'East Hertfordshire',
    'Rushmoor',
    'Surrey Heath',
    'Woking',
    'Bracknell Forest',
    'Windsor and Maidenhead',
    'Slough',
    'Runnymede',
    'Spelthorne',
    'Hounslow',
    'Epsom and Ewell',
    'Merton',
    'Kingston upon Thames',
    'Elmbridge',
    'South Bucks',
    'Chiltern',
    'Dacorum',
    'Three Rivers',
    'Watford',
    'Hertsmere',
    'St Albans',
    'Harrow',
    'Brent',
    'Barking and Dagenham',
    'Thurrock',
    'Ealing',
    'Kensington and Chelsea',
    'Camden',
    'Hackney',
    'Welwyn Hatfield',
    'Broxbourne',
    'Epping Forest',
    'Harlow',
    'Brentwood',
    'Basildon',
    'Chelmsford',
    'City of London',
    'Hammersmith and Fulham',
    'Castle Point',
    'Rochford',
    'Southend-on-Sea',
    'Rochford'
  ],
  midlands: [
    'Herefordshire, County of',
    'Shropshire',
    'Telford and Wrekin',
    'Tewkesbury',
    'Cheltenham',
    'Cotswold',
    'South Kesteven',
    'Malvern Hills',
    'Worcester',
    'Wychavon',
    'Redditch',
    'Bromsgrove',
    'Wyre Forest',
    'Dudley',
    'Sandwell',
    'Wolverhampton',
    'Walsall',
    'South Staffordshire',
    'Cannock Chase',
    'Stafford',
    'Newcastle-under-Lyme',
    'Stoke-on-Trent',
    'Staffordshire Moorlands',
    'Derbyshire Dales',
    'Stratford-on-Avon',
    'Warwick',
    'Solihull',
    'Birmingham',
    'Coventry',
    'Nuneaton and Bedworth',
    'North Warwickshire',
    'Tamworth',
    'Lichfield',
    'East Staffordshire',
    'South Derbyshire',
    'Derby',
    'Erewash',
    'Amber Valley',
    'North East Derbyshire',
    'Chesterfield',
    'Bolsover',
    'Mansfield',
    'Ashfield',
    'Gedling',
    'Nottingham',
    'Broxtowe',
    'Rushcliffe',
    'North West Leicestershire',
    'Hinckley and Bosworth',
    'Leicester',
    'Charnwood',
    'Melton',
    'Oadby and Wigston',
    'Blaby',
    'Rugby',
    'Harborough',
    'Rutland'
  ],
  northEast: [
    'Northumberland',
    'Newcastle upon Tyne',
    'North Tyneside',
    'Gateshead',
    'South Tyneside',
    'Sunderland',
    'County Durham',
    'Hartlepool',
    'Stockton-on-Tees',
    'Darlington',
    'Middlesbrough',
    'Redcar and Cleveland'
  ],
  northWest: [
    'South Lakeland',
    'Lancaster',
    'Wyre',
    'Blackpool',
    'Fylde',
    'Preston',
    'Ribble Valley',
    'Hyndburn',
    'Blackburn with Darwen',
    'Chorley',
    'West Lancashire',
    'Sefton',
    'Craven',
    'South Ribble',
    'Wigan',
    'Bolton',
    'St. Helens',
    'Knowsley',
    'Liverpool',
    'Wirral',
    'Cheshire West and Chester',
    'Warrington',
    'Knowsley',
    'Halton',
    'Cheshire East',
    'High Peak',
    'Trafford',
    'Salford',
    'Wigan',
    'Bury',
    'Rossendale',
    'Pendle',
    'Burnley',
    'Pendle',
    'Oldham',
    'Tameside',
    'Rochdale',
    'Manchester',
    'Stockport'
  ],
  scotland: [
    'Clackmannanshire',
    'Shetland Islands',
    'Highland',
    'Orkney Islands',
    'Eilean Siar',
    'Moray',
    'Aberdeenshire',
    'Aberdeen City',
    'Angus',
    'Dundee City',
    'Perth and Kinross',
    'Argyll and Bute',
    'Stirling',
    'Fife',
    'Falkirk',
    'Renfrewshire',
    'East Dunbartonshire',
    'West Dunbartonshire',
    'North Ayrshire',
    'South Ayrshire',
    'East Ayrshire',
    'South Lanarkshire',
    'Midlothian',
    'East Lothian',
    'City of Edinburgh',
    'West Lothian',
    'North Lanarkshire',
    'Glasgow City',
    'East Renfrewshire',
    'Inverclyde'
  ],
  southAndSouthEast: [
    'Purbeck',
    'North Dorset',
    'Poole',
    'East Dorset',
    'Christchurch',
    'New Forest',
    'Isle of Wight',
    'Portsmouth',
    'Gosport',
    'Fareham',
    'Southampton',
    'Test Valley',
    'Eastleigh',
    'Winchester',
    'East Hampshire',
    'Chichester',
    'Havant',
    'Arun',
    'Worthing',
    'Adur',
    'Mid Sussex',
    'Brighton and Hove',
    'Lewes',
    'Eastbourne',
    'Wealden',
    'Rother',
    'Hastings',
    'Ashford',
    'Shepway',
    'Bournemouth',
    'Horsham',
    'Dover',
    'Thanet',
    'Canterbury',
    'Swale',
    'Basingstoke and Deane',
    'West Berkshire',
    'Swindon',
    'Vale of White Horse',
    'Maidstone',
    'Tunbridge Wells',
    'Crawley',
    'Tonbridge and Malling',
    'Hart',
    'South Oxfordshire',
    'Oxford',
    'Cherwell',
    'West Oxfordshire',
    'Reading',
    'Wokingham',
    'Wycombe',
    'Aylesbury Vale'
  ],
  southWest: [
    'Cornwall',
    'Plymouth',
    'South Hams',
    'Torbay',
    'Torridge',
    'North Devon',
    'West Devon',
    'Mid Devon',
    'Teignbridge',
    'Exeter',
    'East Devon',
    'West Dorset',
    'Weymouth and Portland'
  ],
  wales: [
    'Pembrokeshire',
    'Carmarthenshire',
    'Conwy',
    'Denbighshire',
    'Powys',
    'Ceredigion',
    'Gwynedd',
    'Isle of Anglesey',
    'Swansea',
    'Monmouthshire',
    'Torfaen',
    'Newport',
    'Cardiff',
    'Vale of Glamorgan',
    'Bridgen',
    'Neath Port Talbot',
    'Rhondda Cynon Taf',
    'Merthyr Tydfil',
    'Blaenau Gwent',
    'Caerphilly',
    'Bridgend',
    'Flintshire',
    'Wrexham'
  ],
  west: [
    'West Somerset',
    'Taunton Deane',
    'South Somerset',
    'Sedgemoor',
    'Mendip',
    'North Somerset',
    'Bath and North East Somerset',
    'Bristol, City of',
    'South Gloucestershire',
    'Stroud',
    'Gloucester',
    'Forest of Dean',
    'Wiltshire'
  ],
  yorkshire: [
    'East Riding of Yorkshire',
    'Scarborough',
    'Ryedale',
    'Kingston upon Hull, City of',
    'North Lincolnshire',
    'North East Lincolnshire',
    'East Lindsey',
    'Boston',
    'South Holland',
    'West Lindsey',
    'North Kesteven',
    'Lincoln',
    'Newark and Sherwood',
    'Hambleton',
    'Richmondshire',
    'Calderdale',
    'Bradford',
    'Harrogate',
    'Leeds',
    'Kirklees',
    'Selby',
    'York',
    'Wakefield',
    'Barnsley',
    'Doncaster',
    'Bassetlaw',
    'Rotherham',
    'Sheffield'
  ]
};

const colors = {
  border: '#050505',
  east: '#131313',
  london: '#2d2d2d',
  midlands: '#494848',
  northEast: '#5a5a5a',
  northWest: '#6f6f6f',
  scotland: '#868685',
  southAndSouthEast: '#9e9e9e',
  southWest: '#bfbdbd',
  west: '#d1d2d1',
  wales: '#e7e4e4',
  yorkshire: '#eaeaec'
};

const labels = {
  border: 'Border',
  east: 'East',
  london: 'London',
  midlands: 'Midlands',
  northEast: 'North East',
  northWest: 'North West',
  scotland: 'Scotland',
  southAndSouthEast: 'South and South East',
  southWest: 'South West',
  west: 'West',
  wales: 'Wales',
  yorkshire: 'Yorkshire'
};
let tooltipTimeout: number;
const UkMapChart: FC<any> = ({
  data,
  metric,
  onItemClick,
  color,
  emptyDataMessage,
  showMoreDetail,
  positionLegend = 'bottom'
}) => {
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip
  } = useTooltip<TooltipData>();
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const windowSize = useWindowSize();
  const svgRef = useRef(null);
  const isEmpty = !data || data.length === 0;
  const chartId = useRef(uniqid());

  const centerX = width / 2;
  const centerY = height / 2;

  const max = (data && Math.max(...data.map((item) => item[metric]))) || 0;
  const min = (data && Math.min(...data.map((item) => item[metric]))) || 0;

  const chartRef = useCallback(
    (node) => {
      if (node != null) {
        setWidth(node.getBoundingClientRect().width);
        setHeight(node.getBoundingClientRect().height);
      }
    },
    [windowSize]
  );
  const colorsRange = getMapColors(color);
  const colorScale = useMemo(
    () =>
      scaleLinear({
        domain: [
          metric === METRICS.AFFINITY ? min : min * 100,
          metric === METRICS.AFFINITY ? max : max * 100
        ],
        range: colorsRange
      }),
    [metric, min, max, color]
  );

  const getArea = useCallback(
    (item) => {
      let color = null;
      let label = null;
      Object.keys(districts).forEach((area) => {
        if (districts[area].includes(item)) {
          color = colors[area];
          label = labels[area];
        }
      });
      return {
        label,
        color
      };
    },
    [districts, colors, labels]
  );

  const getFillColor = useCallback(
    (item) => {
      if (!metric) {
        return item.color;
      }

      let value = item[metric] || 0;
      if (metric !== METRICS.AFFINITY) {
        value = value * 100;
      }
      return colorScale(value);
    },
    [metric, colorScale]
  );

  const handleMouseMove = useCallback(
    (event, item) => {
      if (tooltipTimeout) clearTimeout(tooltipTimeout);
      if (!svgRef.current) return;
      const point = localPoint(svgRef.current, event);
      showTooltip({
        tooltipData: {
          ...item,
          color: getFillColor(item)
        },
        tooltipTop: point?.y,
        tooltipLeft: point?.x
      });
    },
    [svgRef, showTooltip, getFillColor]
  );

  const handleMouseLeave = useCallback(
    (event) => {
      tooltipTimeout = window.setTimeout(() => {
        hideTooltip();
      }, 0);
    },
    [hideTooltip]
  );

  const tooltipContent = useMemo(() => {
    if (showMoreDetail) {
      return (
        <React.Fragment>
          {tooltipOpen && tooltipData && (
            <ChartTooltip
              opened={tooltipOpen}
              top={tooltipTop || 0}
              left={tooltipLeft || 0}
              data={tooltipData}
            >
              {isReach(metric) && (
                <div>
                  <span>Reach: </span>
                  {FormatUtils.formattedValue(
                    getPopulationPerc(tooltipData),
                    METRICS.REACH
                  )}
                </div>
              )}
              {metric === METRICS.AFFINITY && (
                <div>
                  <span>Affinity: </span>
                  {FormatUtils.formattedValue(
                    tooltipData.target_audience_affinity,
                    METRICS.AFFINITY
                  )}
                </div>
              )}
              {metric === METRICS.BASE && (
                <div>
                  <span>Base: </span>
                  {FormatUtils.formattedValue(
                    tooltipData.base_population_perc,
                    METRICS.BASE
                  )}
                </div>
              )}
              {metric === METRICS.PENETRATION && (
                <div>
                  <span>Penetration: </span>
                  {FormatUtils.formattedValue(
                    tooltipData.penetration,
                    METRICS.PENETRATION
                  )}
                </div>
              )}
              {metric === METRICS.RELEVANCY && (
                <div>
                  <span>Relevance: </span>
                  {FormatUtils.formattedValue(
                    tooltipData.relevancy,
                    METRICS.RELEVANCY
                  )}
                </div>
              )}
            </ChartTooltip>
          )}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        {tooltipOpen && tooltipData && (
          <StyledUkMapChartTooltip top={tooltipTop} left={tooltipLeft}>
            <div>{tooltipData && tooltipData.label}</div>
          </StyledUkMapChartTooltip>
        )}
      </React.Fragment>
    );
  }, [
    showMoreDetail,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    metric,
    tooltipOpen
  ]);

  if (isEmpty) {
    return (
      <StyledUkMapChartWrapper>
        <BoxV2 minWidth='100%'>
          <EmptyStateV2
            icon='analytics'
            mainText={emptyDataMessage}
            size='large'
          />
        </BoxV2>
      </StyledUkMapChartWrapper>
    );
  }

  return (
    <StyledUkMapChartWrapper ref={chartRef}>
      <svg width={width} height={height} ref={svgRef}>
        <Albers
          data={regions}
          center={[0, 54]}
          rotate={[4.4, 0]}
          scale={(width + height) * 2}
          translate={[width / 2, height / 2]}
        >
          {({ features }) =>
            features.map(({ feature, path, projection }, i) => {
              const area = getArea(feature.properties.LAD13NM);
              const item =
                data && data.find((item) => item.label === area.label);

              if (!item) {
                return null;
              }
              const fullItem = { ...item, color: area.color };
              return (
                <StyledUkMapChartFeature
                  key={`region-${chartId.current}-${feature.properties.LAD13CD}`}
                  metric={metric}
                >
                  <path
                    d={path}
                    stroke={getFillColor(fullItem) || '#ffffff'}
                    fill={getFillColor(fullItem) || '#c6c3c3'}
                    className={`region-${chartId.current}-${feature.properties.LAD13CD}`}
                    onMouseMove={(event) => handleMouseMove(event, fullItem)}
                    onMouseLeave={(event) => handleMouseLeave(event)}
                    onClick={() => {
                      onItemClick && onItemClick(fullItem);
                    }}
                  />
                </StyledUkMapChartFeature>
              );
            })
          }
        </Albers>
        {metric && (
          <Group
            top={positionLegend === 'left' ? centerY - 100 : height - 40}
            left={positionLegend === 'left' ? 100 : centerX - 100}
          >
            <LinearGradient
              from={colorsRange[0]}
              to={colorsRange[1]}
              id={`legend-gradient-${color}`}
              vertical={positionLegend === 'left'}
            />
            <Text
              fontSize='10px'
              fontWeight='bold'
              textAnchor='middle'
              verticalAnchor='middle'
              fill='#000000'
              width={100}
              height={20}
              x={positionLegend === 'left' ? 10 : 0}
              y={-10}
            >
              {FormatUtils.formattedValue(min, metric)}
            </Text>
            <Text
              fontSize='10px'
              fontWeight='bold'
              textAnchor='middle'
              verticalAnchor='middle'
              fill='#000000'
              width={100}
              height={20}
              x={positionLegend === 'left' ? 10 : 200}
              y={positionLegend === 'left' ? 210 : -10}
            >
              {`${FormatUtils.formattedValue(max, metric)}`}
            </Text>
            <rect
              width={positionLegend === 'left' ? 20 : 200}
              height={positionLegend === 'left' ? 200 : 20}
              fill={`url(#legend-gradient-${color})`}
            />
            <Text
              fontSize='10px'
              fontWeight='bold'
              textAnchor='middle'
              verticalAnchor='middle'
              fill='#000000'
              width={100}
              height={20}
              x={positionLegend === 'left' ? 40 : 100}
              y={positionLegend === 'left' ? 100 : 30}
            >
              {MAP_CHART_FIELDS_LABEL[metric]}
            </Text>
          </Group>
        )}
      </svg>
      {tooltipContent}
    </StyledUkMapChartWrapper>
  );
};

export default UkMapChart;
